import * as React from 'react';
import InputBox from 'src/components/shared/InputBox';
import SelectMenu from 'src/components/shared/SelectMenu';
import { ExternalData } from 'src/utils/customInterface';
const USStates: Array<ExternalData> = require('src/api/data/states.json');
import Clinic from 'src/models/clinic';
import { checkErrorForm } from 'src/utils/ehrValidators';
import { validationRulesClinic } from '../Provider/validationRules';
import * as uuid from 'uuid';
import * as stringSimilarity from 'string-similarity';
import * as _ from 'lodash';
import Facility from 'src/models/facility';
import { GeneralAddress, GoogleMapAPI } from 'src/utils/googleMapAPI';
import { reactGoogleAPIKey } from 'src/appconfig';

interface Props {
    triggerCheckError: boolean;
    retainClinics?: Array<Clinic>; // when submiting failed
    updateClinics: (clinics: Array<Clinic>, hasError: boolean) => void;

    // nearby facilities
    clinicName: string;
    facilities: Array<Facility>; // this is the nearby facilities. Used to link
    loadingFacilities: boolean;
    showDuplicateAddressMsg: boolean;
}

interface State {
    clinics: Array<Clinic>;
    showNearbyFacilities: Array<string>; // facilityId
    triggerClearError: boolean;
}

class ClinicAddress extends React.Component<Props, State> {
    private googleMapAPI: GoogleMapAPI;
    // private autocomplete: google.maps.places.Autocomplete | null = null;

    constructor(props: Props) {
        super(props);
        this.state = this.getInitialState();
        this.googleMapAPI = new GoogleMapAPI();
    }

    componentDidMount() {
        if (reactGoogleAPIKey && reactGoogleAPIKey!==''){
            this.googleMapAPI.loadGoogleMap(reactGoogleAPIKey, this.initAutocompleteClinicAddress);
        }
    }

    componentWillUnmount() {
        // Unload script to avoid multiple Google scripts loaded warning
        this.googleMapAPI.unloadGoogleMap();
    }

    getInitialState = (): State => {
        const { retainClinics } = this.props;
        return {
            clinics: retainClinics && retainClinics.length > 0 ? retainClinics : [this.initialClinic()],
            showNearbyFacilities: [],
            triggerClearError: false
        };
    }

    initialClinic = (): Clinic => {
        let clinic: Clinic = {
            id: uuid.v4(),
            addressLine1: '',
            city: '',
            state: '',
            zipCode: '',
            phone: '',
            fax: '',
            facilityId: '',
        };
        return clinic;
    }

    handleChange = (id: string, value: string, field: string): void => {
        const { clinics } = this.state;
        let currClinics: Array<Clinic> = Object.assign([], clinics);
        let findIndex = currClinics.findIndex(e => e.id === id);

        if (findIndex !== -1) {
            let newClinic: Clinic = {
                ...currClinics[findIndex],
                [field]: value
            };
            currClinics[findIndex] = newClinic;
            this.props.updateClinics(currClinics, checkErrorForm(validationRulesClinic, newClinic));
            this.setState({ clinics: currClinics });
        }
    }

    addAddress = (): void => {
        let currClinics: Array<Clinic> = Object.assign([], this.state.clinics);
        currClinics.push(this.initialClinic());
        this.setState({
            clinics: currClinics
        });
    }

    deleteAddress = (clinic: Clinic): void => {
        let currClinics = this.state.clinics;
        currClinics.splice(currClinics.indexOf(clinic), 1);
        // Check error the rest clinics
        let hasError: boolean = false;
        for (let index = 0; index < currClinics.length; index++) {
            if (checkErrorForm(validationRulesClinic, currClinics[index])) {
                hasError = true;
                break;
            }
        }
        this.props.updateClinics(currClinics, hasError);
        this.setState({
            clinics: currClinics
        });
    }

    showNearbyFacility = (id: string): void => {
        const { showNearbyFacilities } = this.state;
        let newNearbyFacilities = [...showNearbyFacilities];
        newNearbyFacilities.push(id);
        this.setState({ showNearbyFacilities: newNearbyFacilities });
    }

    hideNearbyFacility = (id: string): void => {
        const { showNearbyFacilities } = this.state;
        let newNearbyFacilities = [...showNearbyFacilities];
        // if hide dropdown => need to delay for click item
        setTimeout(
            () => {
                newNearbyFacilities = newNearbyFacilities.filter(e => e !== id);
                this.setState({ showNearbyFacilities: newNearbyFacilities });
            },
            200);
    }

    linkFacility = (nearbyFacility: Facility, facilityId: string) => {
        const { clinics } = this.state;
        let newClinics = [...clinics];
        let index = newClinics.findIndex(e => e.id === facilityId);
        if (index > -1) {
            newClinics[index].addressLine1 = nearbyFacility.addressLine1;
            newClinics[index].city = nearbyFacility.city;
            newClinics[index].state = nearbyFacility.state;
            newClinics[index].zipCode = nearbyFacility.zipcode;
            newClinics[index].phone = nearbyFacility.phoneNumber;
            newClinics[index].fax = nearbyFacility.faxNumber;
            newClinics[index].facilityId = nearbyFacility.id;
            this.setState((prevState: State) => { return { ...prevState, clinics: newClinics, triggerClearError: !prevState.triggerClearError }; });
            this.props.updateClinics(newClinics, checkErrorForm(validationRulesClinic, newClinics[index]));
        }
    }

    clearFacilityLink = (facilityId: string) => {
        const { clinics } = this.state;
        let newClinics = [...clinics];
        let index = newClinics.findIndex(e => e.id === facilityId);
        if (index > -1) {
            newClinics[index].addressLine1 = '';
            newClinics[index].city = '';
            newClinics[index].state = '';
            newClinics[index].zipCode = '';
            newClinics[index].phone = '';
            newClinics[index].fax = '';
            newClinics[index].facilityId = '';
            this.setState({ clinics: newClinics });
            this.props.updateClinics(newClinics, checkErrorForm(validationRulesClinic, newClinics[index]));
        }
    }

    sortSimilarityFacilities = (facilities: Facility[], facility: Clinic): Facility[] => {
        let newFacilities = [...facilities];
        let originAddress = `${facility.addressLine1}, ${facility.city}, ${facility.state}, ${facility.zipCode}`.toLowerCase();
        newFacilities.forEach(fa => {
            let compareAddress = `${fa.addressLine1}, ${fa.city}, ${fa.state}, ${fa.zipcode}`.toLowerCase();
            fa.similarity = stringSimilarity.compareTwoStrings(compareAddress, originAddress);
        });
        let sortSimilarity = _.orderBy(newFacilities, 'similarity', 'desc');
        return sortSimilarity;
    }

    haveSimilarityFacility = (facilities: Facility[], facility: Clinic): boolean => {
        let originAddress = `${facility.addressLine1}, ${facility.city}, ${facility.state}, ${facility.zipCode}`.toLowerCase();
        if (facilities && facilities.length > 0) {
            let bestMatch = stringSimilarity.findBestMatch(originAddress, facilities.map(e => `${e.addressLine1}, ${e.city}, ${e.state}, ${e.zipcode}`.toLowerCase())); // Case sensitive
            return bestMatch.bestMatch.rating > 0.8;
        }
        return false;
    }

    initAutocompleteClinicAddress = () => {
        // console.log('Init Autocomplete clinic address starts');
        // var { clinics } = this.state;

        // clinics.length > 0 && clinics.map((clinic, clinicIndex) => {
        //     const clnicAddress1InputId= `txtClinicAddress-${clinicIndex}`;
        //     this.googleMapAPI.initAutocomplete(clnicAddress1InputId, this.autocomplete, (place, generalAddress) => this.handleClinicAddressSelected(generalAddress, clinicIndex));
        // });
    }

    handleClinicAddressSelected = (
        { addressLine1, city, state, zipCode }: GeneralAddress,
        clinicIndex: number
    ) => {
        this.setState((prevState) => {
            const updatedClinics = prevState.clinics.map((item, index) => {
                if (index === clinicIndex) {
                    return {
                        ...item,
                        addressLine1,
                        city,
                        state,
                        zipCode,
                    };
                }
                return item;
            });
    
            return {
                clinics: updatedClinics, // Update the clinics array
            };
        });
    }    

    render(): JSX.Element {
        const { triggerCheckError, facilities, loadingFacilities, showDuplicateAddressMsg } = this.props;
        const { clinics, showNearbyFacilities, triggerClearError } = this.state;

        return (
            <div className="col-auto">
                <div className="autoscroll-1 border-cont" style={{ borderRadius: '0.25rem', maxHeight: '150px' }}>
                    {
                        clinics.length > 0 && clinics.map((clinic, clinicIndex) => (
                            <div key={clinic.id}>
                                <div className="form-group row">
                                    <label className="col-auto col-form-label" htmlFor="txtClinicAddress">&nbsp;Address {clinicIndex + 1}</label>
                                    <div className="col-auto">
                                        <InputBox
                                            id={`txtClinicAddress-${clinicIndex}`}
                                            extendClass={['', 'width350']}
                                            field="addressLine1"
                                            label="Address"
                                            value={clinic.addressLine1}
                                            rules={validationRulesClinic.addressLine1}
                                            onChange={(value, field) => (this.handleChange(clinic.id, value, field))}
                                            triggerCheckError={triggerCheckError}
                                            triggerClearError={triggerClearError}
                                            disabled={!!clinic.facilityId}                                           
                                            autoComplete="new-clinic-address"
                                        />
                                    </div>
                                    <label className="col-auto col-form-label" htmlFor="txtClinicCity">City</label>
                                    <div className="col-auto">
                                        <InputBox
                                            id="txtClinicCity"
                                            extendClass={['', 'width115']}
                                            field="city"
                                            label="City"
                                            value={clinic.city}
                                            rules={validationRulesClinic.city}
                                            onChange={(value, field) => (this.handleChange(clinic.id, value, field))}
                                            triggerCheckError={triggerCheckError}
                                            triggerClearError={triggerClearError}
                                            disabled={!!clinic.facilityId}
                                            autoComplete="new-clinic-city"
                                        />
                                    </div>
                                    <label className="col-auto col-form-label" htmlFor="sbClinicState">State</label>
                                    <div className="col-auto">
                                        <SelectMenu
                                            id="sbClinicState"
                                            extendClass={['', 'width115']}
                                            field="state"
                                            label="State"
                                            value={clinic.state}
                                            rules={validationRulesClinic.state}
                                            onChange={(value, field) => (this.handleChange(clinic.id, value, field))}
                                            triggerCheckError={triggerCheckError}
                                            triggerClearError={triggerClearError}
                                            disabled={!!clinic.facilityId}
                                        >
                                            <option value="">SELECT</option>
                                            {USStates.map(state => <option key={state.code} value={state.code}>{state.name}</option>)}
                                        </SelectMenu>
                                    </div>
                                    <label className="col-auto col-form-label" htmlFor="txtClinicZipcode">Zip Code</label>
                                    <div className="col-auto">
                                        <InputBox
                                            id="txtClinicZipcode"
                                            extendClass={['', 'width75']}
                                            field="zipCode"
                                            label="ZipCode"
                                            value={clinic.zipCode}
                                            rules={validationRulesClinic.zipCode}
                                            onChange={(value, field) => (this.handleChange(clinic.id, value, field))}
                                            triggerCheckError={triggerCheckError}
                                            triggerClearError={triggerClearError}
                                            disabled={!!clinic.facilityId}
                                            autoComplete="new-clinic-zipcode"
                                        />
                                    </div>
                                    <label className="col-auto col-form-label" htmlFor="txtClinicPhone">Phone</label>
                                    <div className="col-auto">
                                        <InputBox
                                            id="txtClinicPhone"
                                            extendClass={['', 'width125']}
                                            field="phone"
                                            label="Phone"
                                            rules={validationRulesClinic.phone}
                                            value={clinic.phone}
                                            placeHolder="(xxx) xxx-xxxx"
                                            mask="(999) 999-9999"
                                            onChange={(value, field) => (this.handleChange(clinic.id, value, field))}
                                            triggerCheckError={triggerCheckError}
                                            triggerClearError={triggerClearError}
                                            disabled={!!clinic.facilityId}
                                            autoComplete="new-clinic-phone"
                                        />
                                    </div>
                                    <label className="col-auto col-form-label" htmlFor="txtClinicFax">Fax</label>
                                    <div className="col-auto">
                                        <InputBox
                                            id="txtClinicFax"
                                            extendClass={['', 'width125']}
                                            field="fax"
                                            label="Fax"
                                            rules={validationRulesClinic.fax}
                                            value={clinic.fax}
                                            placeHolder="(xxx) xxx-xxxx"
                                            mask="(999) 999-9999"
                                            onChange={(value, field) => (this.handleChange(clinic.id, value, field))}
                                            triggerCheckError={triggerCheckError}
                                            triggerClearError={triggerClearError}
                                            disabled={!!clinic.facilityId}
                                            autoComplete="new-clinic-fax"
                                        />
                                    </div>
                                    <div className="col-auto">
                                        {clinicIndex !== 0 ? <button className="btn btn-sm btn-danger col-auto" type="button" onClick={() => this.deleteAddress(clinic)}><i className="fa fa-trash" /> Delete</button>
                                            : <button className="btn btn-sm btn-light col-auto" type="button" onClick={this.addAddress}><i className="fa fa-plus" /> Add more address</button>}
                                    </div>
                                </div>
                                {
                                    loadingFacilities ? <span className="spinner-border text-info custom-loading-inline" role="status" aria-hidden="true" /> :
                                        facilities && facilities.length > 0 ?
                                            <div className="nearby-facility-control">
                                                <button className="btn btn-sm btn-light dropdown-toggle" type="button" onClick={() => this.showNearbyFacility(clinic.id)} onBlur={() => this.hideNearbyFacility(clinic.id)}>Show Nearby Facilities</button>
                                                {
                                                    showNearbyFacilities.includes(clinic.id) &&
                                                    <div className="custom-dropdown-list">
                                                        {
                                                            this.sortSimilarityFacilities(facilities, clinic).map(facility => (
                                                                <p key={'nearby_facility_' + facility.id}>
                                                                    {facility.addressLine1}, {facility.city}, {facility.state}, {facility.zipcode}
                                                                    <button className="btn btn-sm btn-light margin-left-5" type="button" onClick={() => this.linkFacility(facility, clinic.id)}>Link facility</button>
                                                                </p>
                                                            ))
                                                        }
                                                    </div>
                                                }
                                                {
                                                    clinic.facilityId &&
                                                    <button className="btn btn-sm btn-light margin-left-5" type="button" onClick={() => this.clearFacilityLink(clinic.id)}><i className="fa fa-trash" /> Clear link</button>
                                                }
                                                {
                                                    // If clinic.facilityId is empty => no link. We need to check the highest similarity.
                                                    // If haveSimilarityFacility() > 0.8 => maybe same facility => display warning "need to link the facility"
                                                    !clinic.facilityId && this.haveSimilarityFacility(facilities, clinic) &&
                                                    <p className="error-text">Warning: This facility maybe exists in the system. Please check and use the exist facility by click on the "Link facility" button in the Nearby Facilities list, or we will create a new facility.</p>
                                                }
                                            </div>
                                            : ''
                                }

                            </div>
                        ))
                    }
                    {
                        clinics && clinics.length > 1 && showDuplicateAddressMsg &&
                        <p className="error-text">Please don’t add the same address.</p>
                    }
                </div>
            </div>
        );
    }
}

export default ClinicAddress;


// WEBPACK FOOTER //
// ./src/components/ClinicInformation/ClinicAddress.tsx